import { useState, useEffect, useMemo, useRef } from "react"
import { Button } from "@/components/ui/button"
import { Menu, X, Building2, ChevronDown, ChevronRight, User, Settings, Building, Bot, Lightbulb, Wrench, Users, LogOut, CreditCard } from "lucide-react"
import orbys360Icon from "@/assets/orbys360-icon.svg"
import { cn } from "@/lib/utils"
import { useNavigate, useLocation } from "react-router-dom"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu"
import { useUser, useClerk, useOrganization } from "@clerk/clerk-react"
import { track } from "@/lib/analytics"
import { useUserRole } from "@/hooks/useUserRole"
import { usePendingOrganization } from "@/hooks/usePendingOrganization"
import { toast } from "sonner"
import { getMyProviderProfile } from "@/services/providers"

type PageContext = 'aifirstgcc' | 'agent-marketplace' | 'thought-leadership'

interface UnifiedNavigationProps {
  currentPage?: PageContext
  className?: string
}

type NavCTA = {
  text: string
  href: string
  variant: 'primary' | 'outline'
  isProfile?: boolean
  profileMode?: 'provider' | 'gcc'
}

export function UnifiedNavigation({ currentPage, className }: UnifiedNavigationProps) {
  const [isScrolled, setIsScrolled] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [isAgentMenuOpen, setIsAgentMenuOpen] = useState(false)
  const [isThoughtMenuOpen, setIsThoughtMenuOpen] = useState(false)
  const [isToolsMenuOpen, setIsToolsMenuOpen] = useState(false)
  const [isProviderMenuOpen, setIsProviderMenuOpen] = useState(false)
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false)
  // Mobile side navigation expandable sections
  const [expandedMobileSections, setExpandedMobileSections] = useState<string[]>([])
  const hoverCloseTimers = useRef<Record<string, ReturnType<typeof setTimeout> | null>>({})

  const cancelMenuClose = (key: string) => {
    const timeout = hoverCloseTimers.current[key]
    if (timeout) {
      clearTimeout(timeout)
      hoverCloseTimers.current[key] = null
    }
  }

  const scheduleMenuClose = (key: string, setter: (open: boolean) => void) => {
    cancelMenuClose(key)
    hoverCloseTimers.current[key] = setTimeout(() => {
      setter(false)
      hoverCloseTimers.current[key] = null
    }, 120)
  }
  const navigate = useNavigate()
  const location = useLocation()
  const { isSignedIn, user } = useUser()
  const { openUserProfile, openOrganizationProfile, signOut } = useClerk()
  const { organization } = useOrganization()
  const { role } = useUserRole()
  const { isPending, requiresOrganization, isLoaded: isOrgContextLoaded } = usePendingOrganization()

  // Required fields for listing an agent
  const requiredProviderFields = [
    'company_name', 'logo_url', 'about', 'website_url',
    'company_size', 'contact_name', 'contact_email', 'phone_number'
  ]

  // Handle List Agent click with profile validation
  const handleListAgentClick = async (closeMobileMenu?: boolean) => {
    track('cta_list_your_agent_clicked')

    if (!isSignedIn) {
      toast.info('Sign in to list your agent')
      if (closeMobileMenu) setIsMobileMenuOpen(false)
      return
    }

    if (!organization?.id) {
      toast.info('Complete your enabler profile to list agents')
      if (closeMobileMenu) setIsMobileMenuOpen(false)
      return
    }

    try {
      const profile = await getMyProviderProfile()

      if (!profile) {
        toast.info('Complete your enabler profile to list agents')
        if (closeMobileMenu) setIsMobileMenuOpen(false)
        return
      }

      const status = profile.status as string | undefined
      if (status !== 'approved') {
        toast.info(
          status === 'pending'
            ? 'Your profile is pending review. You can list agents once approved.'
            : 'Your profile needs attention before you can list agents.'
        )
        if (closeMobileMenu) setIsMobileMenuOpen(false)
        return
      }

      const missingKeys = requiredProviderFields.filter((key) => {
        const value = (profile as any)[key]
        if (value == null) return true
        if (typeof value === 'string' && value.trim().length === 0) return true
        return false
      })

      if (missingKeys.length > 0) {
        toast.info('Complete your profile details to list agents')
        if (closeMobileMenu) setIsMobileMenuOpen(false)
        return
      }

      // All checks passed - navigate to list agent page
      navigate('/list-your-agent')
      if (closeMobileMenu) {
        setIsMobileMenuOpen(false)
        setExpandedMobileSections([])
      }
    } catch (error) {
      console.error('Error checking profile:', error)
      toast.error('Unable to verify profile. Please try again.')
      if (closeMobileMenu) setIsMobileMenuOpen(false)
    }
  }

  const providerSignupUrl = '/auth?mode=signup&role=provider&next=/get-started'
  const gccSignupUrl = '/auth?mode=signup&role=gcc&next=/gcc-dashboard'
  const providerDashboardPath = '/provider'
  const gccDashboardPath = '/gcc-dashboard'

  // Auto-detect current page if not provided
  const detectedPage: PageContext = currentPage || (() => {
    if (location.pathname === '/' || location.pathname.startsWith('/get-started')) return 'aifirstgcc'
    if (location.pathname.startsWith('/thought-leadership')) return 'thought-leadership'
    return 'agent-marketplace'
  })()

  // Detect if on auth page to hide contextual CTAs
  const isAuthPage = location.pathname === '/auth'
  const desktopNavButtonClass = "group inline-flex h-11 lg:h-12 items-center rounded-full px-3 lg:px-4 xl:px-5 text-slate-700 text-base lg:text-lg font-semibold leading-tight transition-colors hover:bg-white/75 hover:text-slate-900"
  const desktopNavOpenClass = "bg-white/80 text-slate-900 shadow-[0_8px_30px_rgba(15,23,42,0.1)]"

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50)
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const mainNavItems = [
    { label: "aifirstgcc.com", href: "/", icon: Building2, isRoute: true, isBrand: true }
  ]
  const isHome = location.pathname === '/'

  const contextualCTAs: NavCTA[] = useMemo(() => {
    const items: NavCTA[] = []

    const dashboardCTA = (mode: 'provider' | 'gcc'): NavCTA => ({
      text: mode === 'gcc' ? 'GCC Dashboard' : 'Provider Dashboard',
      href: mode === 'gcc' ? gccDashboardPath : providerDashboardPath,
      variant: 'primary',
      isProfile: true,
      profileMode: mode
    })

    if (detectedPage === 'aifirstgcc') {
      if (isSignedIn) {
        items.push(dashboardCTA(role === 'gcc' ? 'gcc' : 'provider'))
      }
      return items
    }

    if (detectedPage === 'agent-marketplace') {
      if (!isSignedIn) {
        items.push({ text: 'Join as Provider', href: providerSignupUrl, variant: 'primary' })
        return items
      }

      if (role === 'gcc') {
        items.push(dashboardCTA('gcc'))
        return items
      }

      items.push({ text: 'List Your Agent', href: '/list-your-agent', variant: 'primary' })
      return items
    }

    return items
  }, [detectedPage, gccDashboardPath, isSignedIn, providerDashboardPath, providerSignupUrl, role])

  // Early return AFTER all hooks to satisfy React Rules of Hooks
  if ((requiresOrganization && !isOrgContextLoaded) || isPending) {
    return null
  }

  // Hide navigation entirely on auth page
  if (isAuthPage) {
    return null
  }

  const toolsMenuItems = [
    { label: 'AI‑First GCC Self Assessment', href: '/tools/self-assessment' },
    { label: 'AI Charter Template', href: '/tools/charter' },
    { label: 'AI Skills Taxonomy', href: '/tools/skills-taxonomy' },
  { label: 'AI Workflow Mapping Kit', href: '/tools/workflow-kit' },
  { label: 'Agent Business Case Template', href: '/tools/business-case' },
  { label: 'AI Readiness Assessment', href: '/tools/ai-readiness-assessment' },
  { label: 'AI Use Case Capture Form', href: '/tools/ai-use-case-capture' },
  { label: 'AI Use Case Prioritization Framework', href: '/tools/ai-use-case-prioritization' },
  { label: 'Explore Agentic AI Use Cases', href: '/use-cases' }
]

  const handleNavigation = (href: string, isRoute: boolean) => {
    if (href === '/list-your-agent') track('cta_list_your_agent_clicked')
    if (href === '/marketplace') track('marketplace_viewed_from_nav')
    if (isRoute) {
      navigate(href)
    }
    setIsMobileMenuOpen(false)
  }

  const toggleMobileSection = (sectionId: string) => {
    setExpandedMobileSections(prev =>
      prev.includes(sectionId)
        ? prev.filter(id => id !== sectionId)
        : [...prev, sectionId]
    )
  }

  const navigateAndCloseMobile = (href: string) => {
    if (href === '/list-your-agent') track('cta_list_your_agent_clicked')
    if (href === '/marketplace') track('marketplace_viewed_from_nav')
    navigate(href)
    setIsMobileMenuOpen(false)
    setExpandedMobileSections([]) // Reset expanded sections
  }

  // Swap Join Today to Profile when authenticated
  return (
    <>
      {/* Desktop Navigation - Floating Pill */}
      <div className={cn("absolute top-0 left-0 right-0 bg-transparent py-6 z-50 pointer-events-none hidden md:block", className)}>
        <nav className={cn(
          "fixed top-4 left-1/2 -translate-x-1/2 z-50 transition-all duration-300 pointer-events-auto",
          "inline-flex w-auto max-w-[calc(100%_-_2rem)] lg:max-w-6xl xl:max-w-7xl",
          "bg-white/80 backdrop-blur-xl border border-white/60 rounded-full",
          "shadow-[0_12px_45px_rgba(15,23,42,0.16)]",
          isScrolled && "bg-white/90 border-white/70 shadow-[0_14px_55px_rgba(15,23,42,0.20)]",
          "px-4 lg:px-6 py-2"
        )}>
          <div className="flex items-center gap-3">
            {/* Brand Section */}
            <div className="shrink-0 md:mr-6">
              <button
                onClick={() => navigate('/')}
                className="flex items-center justify-center hover:opacity-90 transition-opacity duration-200"
                aria-label="Go to homepage"
              >
                <img src={orbys360Icon} alt="Orbys360" className="h-9 w-9 md:h-10 md:w-10" />
              </button>
            </div>

            {/* Desktop Navigation with dropdowns */}
            <div className="flex items-center space-x-0.5 lg:space-x-1 xl:space-x-2 flex-shrink min-w-0">
              {/* Orbyt */}
              <DropdownMenu modal={false} open={isAgentMenuOpen} onOpenChange={setIsAgentMenuOpen}>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className={cn(
                      desktopNavButtonClass,
                      isAgentMenuOpen && desktopNavOpenClass
                    )}
                    onClick={(event) => {
                      event.preventDefault()
                      navigate('/agentsphere')
                      setIsAgentMenuOpen(false)
                    }}
                    onMouseEnter={() => {
                      cancelMenuClose('agent')
                      setIsAgentMenuOpen(true)
                    }}
                    onMouseLeave={() => scheduleMenuClose('agent', setIsAgentMenuOpen)}
                    aria-haspopup="true"
                    aria-expanded={isAgentMenuOpen}
                  >
                    <span>Orbyt</span>
                    <ChevronDown className={cn("ml-1 h-4 w-4 opacity-60 transition-transform", isAgentMenuOpen && "rotate-180")} />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="center"
                  sideOffset={8}
                  className="min-w-[220px]"
                  onMouseEnter={() => {
                    cancelMenuClose('agent')
                    setIsAgentMenuOpen(true)
                  }}
                  onMouseLeave={() => scheduleMenuClose('agent', setIsAgentMenuOpen)}
                >
                  <DropdownMenuItem onClick={() => navigate('/agentsphere')}>About Orbyt</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/marketplace')}>Explore Orbyt</DropdownMenuItem>
                  {role !== 'gcc' && (
                    <DropdownMenuItem onClick={() => handleListAgentClick()}>List Agent</DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Thought Leadership */}
              <DropdownMenu modal={false} open={isThoughtMenuOpen} onOpenChange={setIsThoughtMenuOpen}>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className={cn(
                      desktopNavButtonClass,
                      isThoughtMenuOpen && desktopNavOpenClass
                    )}
                    onClick={(event) => {
                      event.preventDefault()
                      navigate('/thought-leadership')
                      setIsThoughtMenuOpen(false)
                    }}
                    onMouseEnter={() => {
                      cancelMenuClose('thought')
                      setIsThoughtMenuOpen(true)
                    }}
                    onMouseLeave={() => scheduleMenuClose('thought', setIsThoughtMenuOpen)}
                    aria-haspopup="true"
                    aria-expanded={isThoughtMenuOpen}
                  >
                    <span>Thought Leadership</span>
                    <ChevronDown className={cn("ml-1 h-4 w-4 opacity-60 transition-transform", isThoughtMenuOpen && "rotate-180")} />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="center"
                  sideOffset={8}
                  className="min-w-[220px]"
                  onMouseEnter={() => {
                    cancelMenuClose('thought')
                    setIsThoughtMenuOpen(true)
                  }}
                  onMouseLeave={() => scheduleMenuClose('thought', setIsThoughtMenuOpen)}
                >
                  <DropdownMenuItem onClick={() => navigate('/thought-leadership')}>Explore Blogs</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/agentic-ai-thoughtbook')}>Agentic AI Thoughtbook</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Tools */}
              <DropdownMenu modal={false} open={isToolsMenuOpen} onOpenChange={setIsToolsMenuOpen}>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className={cn(
                      desktopNavButtonClass,
                      isToolsMenuOpen && desktopNavOpenClass
                    )}
                    onClick={(event) => {
                      event.preventDefault()
                      navigate('/tools')
                      setIsToolsMenuOpen(false)
                    }}
                    onMouseEnter={() => {
                      cancelMenuClose('tools')
                      setIsToolsMenuOpen(true)
                    }}
                    onMouseLeave={() => scheduleMenuClose('tools', setIsToolsMenuOpen)}
                    aria-haspopup="true"
                    aria-expanded={isToolsMenuOpen}
                  >
                    <span>Tools</span>
                    <ChevronDown className={cn("ml-1 h-4 w-4 opacity-60 transition-transform", isToolsMenuOpen && "rotate-180")} />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="center"
                  sideOffset={8}
                  className="min-w-[280px] md:min-w-[420px] grid gap-1 p-2 md:grid-cols-2"
                  onMouseEnter={() => {
                    cancelMenuClose('tools')
                    setIsToolsMenuOpen(true)
                  }}
                  onMouseLeave={() => scheduleMenuClose('tools', setIsToolsMenuOpen)}
                >
                  <DropdownMenuItem
                    className="md:col-span-2 font-medium text-purple-600 focus:text-purple-700"
                    onClick={() => navigate('/tools')}
                  >
                    Explore Tools
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="md:col-span-2" />
                  {toolsMenuItems.map(item => (
                    <DropdownMenuItem
                      key={item.href}
                      className="text-sm leading-snug md:py-2"
                      onClick={() => navigate(item.href)}
                    >
                      {item.label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Provider Directory */}
              <DropdownMenu modal={false} open={isProviderMenuOpen} onOpenChange={setIsProviderMenuOpen}>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className={cn(
                      desktopNavButtonClass,
                      isProviderMenuOpen && desktopNavOpenClass
                    )}
                    onClick={(event) => {
                      event.preventDefault()
                      navigate('/providers/directory')
                      setIsProviderMenuOpen(false)
                    }}
                    onMouseEnter={() => {
                      cancelMenuClose('providers')
                      setIsProviderMenuOpen(true)
                    }}
                    onMouseLeave={() => scheduleMenuClose('providers', setIsProviderMenuOpen)}
                    aria-haspopup="true"
                    aria-expanded={isProviderMenuOpen}
                  >
                    <span>Provider Directory</span>
                    <ChevronDown className={cn("ml-1 h-4 w-4 opacity-60 transition-transform", isProviderMenuOpen && "rotate-180")} />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="center"
                  sideOffset={8}
                  className="min-w-[220px]"
                  onMouseEnter={() => {
                    cancelMenuClose('providers')
                    setIsProviderMenuOpen(true)
                  }}
                  onMouseLeave={() => scheduleMenuClose('providers', setIsProviderMenuOpen)}
                >
                  <DropdownMenuItem
                    className="font-medium text-purple-600 focus:text-purple-700"
                    onClick={() => navigate('/providers/directory')}
                  >
                    Explore Directory
                  </DropdownMenuItem>
                  {(role !== 'gcc') && (
                    <DropdownMenuItem onClick={() => navigate('/get-started')}>
                      Enlist as Provider
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={() => navigate('/providers')}>About</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Pricing */}
              <Button
                variant="ghost"
                size="sm"
                className={desktopNavButtonClass}
                onClick={() => navigate('/pricing')}
              >
                <span>Pricing</span>
              </Button>
            </div>

            {/* CTA Buttons - Desktop */}
            <div className="hidden md:flex items-center gap-2 lg:gap-3 ml-2 lg:ml-4 pl-2 lg:pl-4 border-l border-white/60">
              {/* Sign In link - only show when not signed in */}
              {!isSignedIn && (
              <Button
                variant="ghost"
                size="sm"
                className="h-11 rounded-full px-4 text-slate-700 font-semibold hover:bg-white/75 hover:text-slate-900"
                onClick={() => navigate('/auth?mode=signin')}
              >
                Sign In
              </Button>
              )}
              {!isSignedIn && contextualCTAs.filter(cta => !(cta as any).isProfile).length === 0 && (
                <DropdownMenu modal={false}>
                  <DropdownMenuTrigger asChild>
                    <Button
                      size="sm"
                      className="h-11 rounded-full px-5 text-base font-semibold bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:shadow-lg"
                    >
                      Join Now
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="center" sideOffset={8} className="min-w-[220px]">
                    <DropdownMenuItem onClick={() => navigate(providerSignupUrl)}>
                      Join as Provider
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate(gccSignupUrl)}>
                      Join as GCC
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}

              {contextualCTAs.map((cta, index) => {
                if ((cta as any).isProfile) {
                  const profileMode = (cta as any).profileMode as 'provider' | 'gcc' | undefined
                  const isGccProfile = profileMode === 'gcc'
                  const dashboardLabel = isGccProfile ? 'GCC Dashboard' : 'Provider Dashboard'
                  const dashboardPath = isGccProfile ? gccDashboardPath : providerDashboardPath
                  return (
                    <DropdownMenu modal={false} key={`profile-${index}`} open={isProfileMenuOpen} onOpenChange={setIsProfileMenuOpen}>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant={cta.variant}
                          size="sm"
                          className={cn(
                            "inline-flex h-11 items-center rounded-full px-5 text-base font-semibold transition-all duration-200",
                            "bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:shadow-lg"
                          )}
                        >
                          {dashboardLabel}
                          <ChevronDown className={cn("ml-1 w-4 h-4 opacity-80 transition-transform", isProfileMenuOpen && "rotate-180")} />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-48">
                        <DropdownMenuItem onSelect={() => navigate(dashboardPath)}>
                          {dashboardLabel}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onSelect={(event) => {
                            event.preventDefault()
                            openUserProfile?.()
                          }}
                        >
                          Manage Account
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onSelect={(event) => {
                            event.preventDefault()
                            openOrganizationProfile?.()
                          }}
                        >
                          Manage Organization
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onSelect={() => {
                            signOut(() => navigate('/'))
                          }}
                        >
                          <LogOut className="w-4 h-4 mr-2" />
                          Log Out
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )
                }

                return (
                  <Button
                    key={index}
                    variant={cta.variant}
                    size="sm"
                    className={cn(
                      "h-11 rounded-full px-5 text-base font-semibold transition-all duration-200 whitespace-nowrap",
                      cta.variant === 'primary'
                        ? "bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:shadow-lg"
                        : "border-white/70 text-slate-800 hover:bg-white/60 hover:border-white/80"
                    )}
                    onClick={() => {
                      if (cta.href === '/list-your-agent') {
                        handleListAgentClick()
                      } else {
                        navigate(cta.href)
                      }
                    }}
                  >
                    {cta.text}
                  </Button>
                )
              })}
            </div>
          </div>
        </nav>
      </div>

      {/* Mobile Navigation - Static Top Bar */}
      <div className={cn("fixed top-0 left-0 right-0 bg-white border-b border-gray-200 z-50 md:hidden", className)}>
        <nav className="flex items-center justify-between px-4 py-3 h-14">
          {/* Brand Section */}
          <button
            onClick={() => navigate('/')}
            className="flex items-center justify-center hover:opacity-80 transition-opacity duration-200"
          >
            <img src={orbys360Icon} alt="Orbys360" className="h-10 w-10" />
          </button>

          {/* Mobile Menu Button */}
            <Button
              variant="ghost"
              size="sm"
              className="p-2"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Toggle navigation menu"
            >
              <Menu className="w-6 h-6 text-slate-700" />
            </Button>
        </nav>
      </div>

      {/* Mobile Side Navigation Panel */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div className="fixed inset-0 bg-black/20 backdrop-blur-sm" onClick={() => setIsMobileMenuOpen(false)} />
          <div className={cn(
            "fixed top-0 right-0 h-full w-80 max-w-[85vw] bg-white shadow-xl transform transition-transform duration-300 ease-out",
            isMobileMenuOpen ? "translate-x-0" : "translate-x-full"
          )}>
            {/* Side Panel Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <div className="flex items-center justify-center">
              <img src={orbys360Icon} alt="Orbys360" className="h-10 w-10" />
            </div>
              <Button
                variant="ghost"
                size="sm"
                className="p-2"
                onClick={() => setIsMobileMenuOpen(false)}
                aria-label="Close navigation menu"
              >
                <X className="w-5 h-5 text-slate-600" />
              </Button>
            </div>

            {/* Navigation Content */}
            <div className="flex flex-col h-full overflow-y-auto">
              {/* Profile Section */}
              <div className="p-4">
                {isSignedIn ? (
                  <div className="mb-4">
                    {/* Profile expandable section */}
                    <div
                      className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg p-3 cursor-pointer"
                      onClick={() => toggleMobileSection('profile')}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                            <User className="w-4 h-4 text-white" />
                          </div>
                          <div>
                            <div className="text-white font-medium">Profile</div>
                            {user?.primaryEmailAddress && (
                              <div className="text-white/80 text-xs">{user.primaryEmailAddress.emailAddress}</div>
                            )}
                          </div>
                        </div>
                        <ChevronDown
                          className={cn(
                            "w-4 h-4 text-white transition-transform duration-200",
                            expandedMobileSections.includes('profile') && "rotate-180"
                          )}
                        />
                      </div>
                    </div>

                    {/* Profile dropdown items */}
                    {expandedMobileSections.includes('profile') && (
                      <div className="mt-2 ml-4 space-y-1 overflow-hidden">
                        <Button
                          variant="ghost"
                          className="w-full justify-start text-slate-600 hover:text-slate-900 py-2"
                          onClick={() => navigateAndCloseMobile(role === 'gcc' ? gccDashboardPath : providerDashboardPath)}
                        >
                          <Building className="w-4 h-4 mr-2" />
                          {role === 'gcc' ? 'GCC Dashboard' : 'Provider Dashboard'}
                        </Button>
                        <Button
                          variant="ghost"
                          className="w-full justify-start text-slate-600 hover:text-slate-900 py-2"
                          onClick={() => {
                            openUserProfile?.()
                            setIsMobileMenuOpen(false)
                          }}
                        >
                          <Settings className="w-4 h-4 mr-2" />
                          Manage Account
                        </Button>
                        <Button
                          variant="ghost"
                          className="w-full justify-start text-slate-600 hover:text-slate-900 py-2"
                          onClick={() => {
                            openOrganizationProfile?.()
                            setIsMobileMenuOpen(false)
                          }}
                        >
                          <Building2 className="w-4 h-4 mr-2" />
                          Manage Organization
                        </Button>
                        <div className="border-t border-gray-200 my-2" />
                        <Button
                          variant="ghost"
                          className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50 py-2"
                          onClick={() => {
                            signOut(() => {
                              setIsMobileMenuOpen(false)
                              navigate('/')
                            })
                          }}
                        >
                          <LogOut className="w-4 h-4 mr-2" />
                          Log Out
                        </Button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="mb-4 space-y-2">
                    <Button
                      className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:shadow-md py-3"
                      onClick={() => navigateAndCloseMobile(providerSignupUrl)}
                    >
                      <User className="w-4 h-4 mr-2" />
                      Join as Provider
                    </Button>
                    <Button
                      variant="outline"
                      className="w-full border-blue-200 text-blue-700 hover:bg-blue-50 py-3"
                      onClick={() => navigateAndCloseMobile(gccSignupUrl)}
                    >
                      <User className="w-4 h-4 mr-2" />
                      Join as GCC
                    </Button>
                  </div>
                )}
              </div>

              {/* Main Navigation Sections */}
              <div className="px-4 space-y-2">
                {/* Orbyt */}
                <div>
                  <div className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50">
                    <button
                      type="button"
                      className="flex items-center space-x-3 text-left"
                      onClick={() => navigateAndCloseMobile('/agentsphere')}
                    >
                      <Bot className="w-5 h-5 text-purple-600" />
                      <span className="font-medium text-slate-800">Orbyt</span>
                    </button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-slate-600 hover:text-slate-900"
                      onClick={(event) => {
                        event.stopPropagation()
                        toggleMobileSection('agent-marketplace')
                      }}
                      aria-label="Toggle Orbyt links"
                    >
                      <ChevronDown
                        className={cn(
                          "w-4 h-4 transition-transform duration-200",
                          expandedMobileSections.includes('agent-marketplace') && "rotate-180"
                        )}
                      />
                    </Button>
                  </div>
                  {expandedMobileSections.includes('agent-marketplace') && (
                    <div className="ml-4 mt-1 space-y-1">
                      <Button variant="ghost" className="w-full justify-start text-slate-600 hover:text-slate-900 py-2" onClick={() => navigateAndCloseMobile('/agentsphere')}>
                        About Orbyt
                      </Button>
                      <Button variant="ghost" className="w-full justify-start text-slate-600 hover:text-slate-900 py-2" onClick={() => navigateAndCloseMobile('/marketplace')}>
                        Explore Orbyt
                      </Button>
                      {role !== 'gcc' && (
                        <Button variant="ghost" className="w-full justify-start text-slate-600 hover:text-slate-900 py-2" onClick={() => handleListAgentClick(true)}>
                          List Agent
                        </Button>
                      )}
                    </div>
                  )}
                </div>

                {/* Thought Leadership */}
                <div>
                  <div className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50">
                    <button
                      type="button"
                      className="flex items-center space-x-3 text-left"
                      onClick={() => navigateAndCloseMobile('/thought-leadership')}
                    >
                      <Lightbulb className="w-5 h-5 text-yellow-600" />
                      <span className="font-medium text-slate-800">Thought Leadership</span>
                    </button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-slate-600 hover:text-slate-900"
                      onClick={(event) => {
                        event.stopPropagation()
                        toggleMobileSection('thought-leadership')
                      }}
                      aria-label="Toggle Thought Leadership links"
                    >
                      <ChevronDown
                        className={cn(
                          "w-4 h-4 transition-transform duration-200",
                          expandedMobileSections.includes('thought-leadership') && "rotate-180"
                        )}
                      />
                    </Button>
                  </div>
                  {expandedMobileSections.includes('thought-leadership') && (
                    <div className="ml-4 mt-1 space-y-1">
                      <Button variant="ghost" className="w-full justify-start text-slate-600 hover:text-slate-900 py-2" onClick={() => navigateAndCloseMobile('/thought-leadership')}>
                        Explore Blogs
                      </Button>
                      <Button variant="ghost" className="w-full justify-start text-slate-600 hover:text-slate-900 py-2" onClick={() => navigateAndCloseMobile('/agentic-ai-thoughtbook')}>
                        Agentic AI Thoughtbook
                      </Button>
                    </div>
                  )}
                </div>

                {/* Tools */}
                <div>
                  <div className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50">
                    <button
                      type="button"
                      className="flex items-center space-x-3 text-left"
                      onClick={() => navigateAndCloseMobile('/tools')}
                    >
                      <Wrench className="w-5 h-5 text-green-600" />
                      <span className="font-medium text-slate-800">Tools</span>
                    </button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-slate-600 hover:text-slate-900"
                      onClick={(event) => {
                        event.stopPropagation()
                        toggleMobileSection('tools')
                      }}
                      aria-label="Toggle Tools links"
                    >
                      <ChevronDown
                        className={cn(
                          "w-4 h-4 transition-transform duration-200",
                          expandedMobileSections.includes('tools') && "rotate-180"
                        )}
                      />
                    </Button>
                  </div>
                  {expandedMobileSections.includes('tools') && (
                    <div className="ml-4 mt-1 space-y-1">
                      <Button variant="ghost" className="w-full justify-start text-purple-600 hover:text-purple-700 py-2 font-medium" onClick={() => navigateAndCloseMobile('/tools')}>
                        Explore Tools
                      </Button>
                      <Button variant="ghost" className="w-full justify-start text-slate-600 hover:text-slate-900 py-2" onClick={() => navigateAndCloseMobile('/tools/self-assessment')}>
                        AI‑First GCC Self Assessment
                      </Button>
                      <Button variant="ghost" className="w-full justify-start text-slate-600 hover:text-slate-900 py-2" onClick={() => navigateAndCloseMobile('/tools/charter')}>
                        AI Charter Template
                      </Button>
                      <Button variant="ghost" className="w-full justify-start text-slate-600 hover:text-slate-900 py-2" onClick={() => navigateAndCloseMobile('/tools/skills-taxonomy')}>
                        AI Skills Taxonomy
                      </Button>
                      <Button variant="ghost" className="w-full justify-start text-slate-600 hover:text-slate-900 py-2" onClick={() => navigateAndCloseMobile('/tools/workflow-kit')}>
                        AI Workflow Mapping Kit
                      </Button>
                      <Button variant="ghost" className="w-full justify-start text-slate-600 hover:text-slate-900 py-2" onClick={() => navigateAndCloseMobile('/tools/business-case')}>
                        Agent Business Case Template
                      </Button>
                      <Button variant="ghost" className="w-full justify-start text-slate-600 hover:text-slate-900 py-2" onClick={() => navigateAndCloseMobile('/tools/ai-readiness-assessment')}>
                        AI Readiness Assessment
                      </Button>
                      <Button variant="ghost" className="w-full justify-start text-slate-600 hover:text-slate-900 py-2" onClick={() => navigateAndCloseMobile('/tools/ai-use-case-capture')}>
                        AI Use Case Capture Form
                      </Button>
                      <Button variant="ghost" className="w-full justify-start text-slate-600 hover:text-slate-900 py-2" onClick={() => navigateAndCloseMobile('/tools/ai-use-case-prioritization')}>
                        AI Use Case Prioritization Framework
                      </Button>
                      <Button variant="ghost" className="w-full justify-start text-slate-600 hover:text-slate-900 py-2" onClick={() => navigateAndCloseMobile('/use-cases')}>
                        Explore Agentic AI Use Cases
                      </Button>
                    </div>
                  )}
                </div>

                {/* Provider Directory */}
                <div>
                  <div className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50">
                    <button
                      type="button"
                      className="flex items-center space-x-3 text-left"
                      onClick={() => navigateAndCloseMobile('/providers/directory')}
                    >
                      <Users className="w-5 h-5 text-purple-600" />
                      <span className="font-medium text-slate-800">Provider Directory</span>
                    </button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-slate-600 hover:text-slate-900"
                      onClick={(event) => {
                        event.stopPropagation()
                        toggleMobileSection('providers')
                      }}
                      aria-label="Toggle provider links"
                    >
                      <ChevronDown
                        className={cn(
                          "w-4 h-4 transition-transform duration-200",
                          expandedMobileSections.includes('providers') && "rotate-180"
                        )}
                      />
                    </Button>
                  </div>
                  {expandedMobileSections.includes('providers') && (
                    <div className="ml-4 mt-1 space-y-1">
                      <Button variant="ghost" className="w-full justify-start text-purple-600 hover:text-purple-700 py-2 font-medium" onClick={() => navigateAndCloseMobile('/providers/directory')}>
                        Explore Directory
                      </Button>
                      {(role !== 'gcc') && (
                        <Button variant="ghost" className="w-full justify-start text-slate-600 hover:text-slate-900 py-2" onClick={() => navigateAndCloseMobile('/get-started')}>
                          Enlist as Provider
                        </Button>
                      )}
                      <Button variant="ghost" className="w-full justify-start text-slate-600 hover:text-slate-900 py-2" onClick={() => navigateAndCloseMobile('/providers')}>
                        About
                      </Button>
                    </div>
                  )}
                </div>

                {/* Pricing */}
                <div>
                  <div className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50">
                    <button
                      type="button"
                      className="flex items-center space-x-3 text-left w-full"
                      onClick={() => navigateAndCloseMobile('/pricing')}
                    >
                      <CreditCard className="w-5 h-5 text-emerald-600" />
                      <span className="font-medium text-slate-800">Pricing</span>
                    </button>
                  </div>
                </div>
              </div>

              {/* Additional CTAs for mobile (only if not signed in and specific pages) */}
              {!isSignedIn && detectedPage !== 'aifirstgcc' && (
                <div className="mt-auto p-4 space-y-2 border-t border-gray-200">
                  {/* Sign In button for mobile */}
                  <Button
                    variant="ghost"
                    className="w-full text-slate-600 hover:text-slate-900 hover:bg-slate-50"
                    onClick={() => navigateAndCloseMobile('/auth?mode=signin')}
                  >
                    Sign In
                  </Button>
                  {contextualCTAs.filter(cta => !(cta as any).isProfile).map((cta, index) => (
                    <Button
                      key={index}
                      variant={cta.variant}
                      className={cn(
                        "w-full transition-all duration-200",
                        cta.variant === 'primary'
                          ? "bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:shadow-md"
                          : "border-gray-300 text-slate-700 hover:bg-gray-50"
                      )}
                      onClick={() => navigateAndCloseMobile(cta.href)}
                    >
                      {cta.text}
                    </Button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  )
}
